/**
 * biometric.js — s114: Face ID / Touch ID confirmation for the video-visit sign-on (canvas V2).
 * Native only (build 80+, @capgo/capacitor-native-biometric + NSFaceIDUsageDescription). On the web, or when the
 * phone has no enrolled biometrics, `biometricAvailable()` answers false and the sign-on offers the PIN instead.
 * Never throws: every failure is "not confirmed".
 */
import { Capacitor } from '@capacitor/core'

// ★★★ s114 (build 81: no Face ID row on his iPhone). A Capacitor plugin object answers EVERY property, `then`
//   included, so returning it from an async function makes `await` treat it as a promise and call a native "then"
//   method; iOS rejects that, the catch turned it into "not available", and Face ID never showed. The plugin is
//   always handed back WRAPPED ({ bio }), never as the resolved value of a promise.
async function plugin() {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('NativeBiometric')) return { bio: null }
  try {
    const mod = await import('@capgo/capacitor-native-biometric')
    return { bio: (mod && mod.NativeBiometric) || null }
  } catch {
    return { bio: null }
  }
}

export async function biometricAvailable() {
  try {
    const { bio: p } = await plugin()
    if (!p) return false
    const r = await p.isAvailable()
    return !!r?.isAvailable
  } catch {
    return false
  }
}

/** true when the person confirmed with Face ID / Touch ID; false on cancel, failure or no plugin. */
export async function verifyBiometric(reason) {
  try {
    const { bio: p } = await plugin()
    if (!p) return false
    await p.verifyIdentity({ reason, title: reason, useFallback: false })
    return true
  } catch {
    return false
  }
}

/**
 * biometricDiagnose — s114 (build 82 on his iPhone: still no Face ID row). Developer-only: the Settings screen shows
 * this after five taps on the Security heading. Every stage of the check, raw; no personal data.
 */
export async function biometricDiagnose() {
  const out = { native: false, platform: '', pluginAvailable: false, headers: [], imported: false, result: null, error: '' }
  try {
    out.native = Capacitor.isNativePlatform()
    out.platform = Capacitor.getPlatform()
    out.pluginAvailable = Capacitor.isPluginAvailable('NativeBiometric')
    const h = (typeof window !== 'undefined' && window.Capacitor && window.Capacitor.PluginHeaders) || []
    out.headers = h.map((x) => x && x.name).filter(Boolean)
    const mod = await import('@capgo/capacitor-native-biometric')
    const bio = mod && mod.NativeBiometric
    out.imported = !!bio
    if (bio) out.result = await bio.isAvailable()
  } catch (e) {
    out.error = String((e && (e.code ? e.code + ': ' : '') + (e.message || e)) || e)
  }
  return out
}

